// The hand-written little-endian wire codec (design D5): encodes the eight
// client->server message variants and decodes the nine server->client variants,
// byte-for-byte per docs/dev/wire-protocol.md. One-directional by design — there is
// no server-message encoder. Implemented over DataView/ArrayBuffer, no library.

import type {
  Ack,
  BeeRecord,
  BlobRecord,
  CellPayload,
  CellRecord,
  ClientMessage,
  CloseDelta,
  CloseEntityKey,
  CloseKeyframe,
  ClosePayload,
  CloseUpsert,
  ColonyMeta,
  CreateType,
  DanceRecord,
  Delta,
  HandEdit,
  Hello,
  InitialProgram,
  Keyframe,
  ManifestSummary,
  MatchResult,
  MatchState,
  MatchStateObs,
  PatchRecord,
  Placement,
  Prompt,
  PromptStatus,
  PromptStatusKind,
  QueenRecord,
  Rejected,
  RejectReason,
  ReplayControlMessage,
  Rollback,
  RuinRecord,
  ScoreboardRow,
  ServerMessage,
  SetBirthType,
  StateBucket,
  StateHistogram,
  Subscribe,
  TypeHistogram,
  TypeMeta,
  VersionHead,
  ViewportRect,
  Welcome,
  WideDelta,
  WideEntityKey,
  WideKeyframe,
  WideUpsert,
} from './messages';
import { ActionClass, Season, Tier } from './messages';

// --- defensive decode bounds (ported from codec.rs) ---
export const MAX_MESSAGE_SIZE = 8 * 1024 * 1024;
export const MAX_TYPES = 12;
export const MAX_STATE_BUCKETS = 13;
export const MAX_VERSION_HEADS = MAX_TYPES;
export const MAX_COLONIES = 150;
export const MAX_CLOSE_RECORDS = 200_000;
export const MAX_WIDE_RECORDS = 40_000;
/** The close-tier dance-record cap (dance-visuals; mirrors `DANCE_CAP_DEFAULT`). */
export const MAX_DANCE_RECORDS = 64;
/** The close-tier cell-record cap (hive-egg-cells; mirrors `CELL_CAP_DEFAULT`). */
export const MAX_CELL_RECORDS = 40_000;
export const MAX_PLACEMENTS = MAX_COLONIES;
export const MAX_DIAGNOSTICS = 64;
export const MAX_TEXT_LEN = 8 * 1024;
export const MAX_IDENT_LEN = 24;
/** Directional-sprite heading buckets (DESIGN §9: 12 × 30°). */
export const FACING_STEPS = 12;

// --- per-type MIN_ENCODED_SIZE constants (mirrors codec.rs's WireDecode::MIN_ENCODED_SIZE,
// used only to bound a collection's count*element_size check before allocating) ---
const MIN_SIZE_U32 = 4;
const MIN_SIZE_BLOB_RECORD = 4 + 2 + 2 + 4 + 1; // 13
const MIN_SIZE_QUEEN_RECORD = 4 + 4 + 2 + 2; // 12
const MIN_SIZE_PATCH_RECORD = 4 + 2 + 2 + 1; // 9 (id + position + quantized pool byte)
const MIN_SIZE_RUIN_RECORD = 4 + 2 + 2; // 8
// 16: id + position + facing/action, then colony_id(4) + a `None` type_id Option
// discriminant(1) + health(1) + nectar(1). A `Some` type_id adds its index byte (17 total).
const MIN_SIZE_BEE_RECORD = 4 + 2 + 2 + 1 + 4 + 1 + 1 + 1;
const MIN_SIZE_DANCE_RECORD = 4 + 2 + 2 + 2; // 10
const MIN_SIZE_CELL_RECORD = 4 + 2 + 2 + 1; // 9 (id + position + packed state byte)
const MIN_SIZE_WIDE_UPSERT = 1 + MIN_SIZE_RUIN_RECORD; // 9
// Bee/Patch/Cell payloads are all 9 B (the smallest close variants); Dance is 10 B.
const MIN_SIZE_CLOSE_UPSERT = 1 + MIN_SIZE_PATCH_RECORD; // 10
const MIN_SIZE_WIDE_ENTITY_KEY = 1 + 4; // 5
const MIN_SIZE_CLOSE_ENTITY_KEY = 1 + 4; // 5
const MIN_SIZE_TYPE_META = 1 + 4 + 4 + 4 + 4; // 17
const MIN_SIZE_VERSION_HEAD = 1 + 4; // 5
const MIN_SIZE_SCOREBOARD_ROW = 4 + 4 + 4; // 12
const MIN_SIZE_PLACEMENT = 4 + 4 + 4 + 4; // 16
const MIN_SIZE_STATE_BUCKET = 4 + 4; // 8
const MIN_SIZE_TYPE_HISTOGRAM = 1 + 4; // 5

/** A typed decode failure, mirroring `waggle-proto::codec::DecodeError`. */
export type DecodeError =
  | { kind: 'truncated' }
  | { kind: 'unknownTag'; tag: number }
  | { kind: 'trailingBytes' }
  | { kind: 'outOfRange'; field: string }
  | { kind: 'tooManyElements'; field: string }
  | { kind: 'oversized' }
  | { kind: 'invalidUtf8' }
  | { kind: 'invalidDiscriminant'; field: string };

/** A decode result: either a value or a typed error (never a thrown string). */
export type DecodeResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: DecodeError };

/** Internal-only: unwinds a decode failure to the top-level try/catch as a typed error,
 * never an untyped throw escaping the public API. */
class DecodeFailure extends Error {
  constructor(readonly error: DecodeError) {
    super(error.kind);
  }
}

function fail(error: DecodeError): never {
  throw new DecodeFailure(error);
}

// ---------------------------------------------------------------------------
// Writer: a growable little-endian byte buffer (encode side).
// ---------------------------------------------------------------------------

class Writer {
  private buf: Uint8Array;
  private view: DataView;
  private len = 0;

  constructor(initialCapacity = 64) {
    this.buf = new Uint8Array(initialCapacity);
    this.view = new DataView(this.buf.buffer);
  }

  private ensure(extra: number): void {
    if (this.len + extra <= this.buf.byteLength) return;
    let capacity = this.buf.byteLength * 2 || 64;
    while (capacity < this.len + extra) capacity *= 2;
    const next = new Uint8Array(capacity);
    next.set(this.buf.subarray(0, this.len));
    this.buf = next;
    this.view = new DataView(this.buf.buffer);
  }

  u8(v: number): void {
    this.ensure(1);
    this.view.setUint8(this.len, v);
    this.len += 1;
  }

  u16(v: number): void {
    this.ensure(2);
    this.view.setUint16(this.len, v, true);
    this.len += 2;
  }

  u32(v: number): void {
    this.ensure(4);
    this.view.setUint32(this.len, v, true);
    this.len += 4;
  }

  u64(v: bigint): void {
    this.ensure(8);
    this.view.setBigUint64(this.len, v, true);
    this.len += 8;
  }

  f32(v: number): void {
    this.ensure(4);
    this.view.setFloat32(this.len, v, true);
    this.len += 4;
  }

  bool(v: boolean): void {
    this.u8(v ? 1 : 0);
  }

  bytes(bytes: Uint8Array): void {
    this.ensure(bytes.length);
    this.buf.set(bytes, this.len);
    this.len += bytes.length;
  }

  string(s: string): void {
    const bytes = new TextEncoder().encode(s);
    this.u32(bytes.length);
    this.bytes(bytes);
  }

  finish(): Uint8Array {
    return this.buf.slice(0, this.len);
  }
}

// ---------------------------------------------------------------------------
// Reader: a bounds-checked little-endian byte cursor (decode side).
// ---------------------------------------------------------------------------

class Reader {
  private offset = 0;
  private readonly view: DataView;

  constructor(private readonly data: Uint8Array) {
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  }

  get remaining(): number {
    return this.data.length - this.offset;
  }

  private need(n: number): void {
    if (this.remaining < n) fail({ kind: 'truncated' });
  }

  u8(): number {
    this.need(1);
    const v = this.view.getUint8(this.offset);
    this.offset += 1;
    return v;
  }

  u16(): number {
    this.need(2);
    const v = this.view.getUint16(this.offset, true);
    this.offset += 2;
    return v;
  }

  u32(): number {
    this.need(4);
    const v = this.view.getUint32(this.offset, true);
    this.offset += 4;
    return v;
  }

  u64(): bigint {
    this.need(8);
    const v = this.view.getBigUint64(this.offset, true);
    this.offset += 8;
    return v;
  }

  f32(): number {
    this.need(4);
    const v = this.view.getFloat32(this.offset, true);
    this.offset += 4;
    return v;
  }

  bool(): boolean {
    const v = this.u8();
    if (v === 0) return false;
    if (v === 1) return true;
    return fail({ kind: 'invalidDiscriminant', field: 'bool' });
  }

  option<T>(decodeOne: () => T): T | null {
    const tag = this.u8();
    if (tag === 0) return null;
    if (tag === 1) return decodeOne();
    return fail({ kind: 'invalidDiscriminant', field: 'Option' });
  }

  tupleF32(): [number, number] {
    const a = this.f32();
    const b = this.f32();
    return [a, b];
  }

  tupleU16(): [number, number] {
    const a = this.u16();
    const b = this.u16();
    return [a, b];
  }

  private stringCapped(maxLen: number, field: string): string {
    const len = this.u32();
    if (len > maxLen) return fail({ kind: 'tooManyElements', field });
    if (len > this.remaining) return fail({ kind: 'truncated' });
    const slice = this.data.subarray(this.offset, this.offset + len);
    this.offset += len;
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(slice);
    } catch {
      return fail({ kind: 'invalidUtf8' });
    }
  }

  text(): string {
    return this.stringCapped(MAX_TEXT_LEN, 'text');
  }

  ident(): string {
    return this.stringCapped(MAX_IDENT_LEN, 'identifier');
  }

  /** Decodes a `u32`-length-prefixed collection: rejects a declared length exceeding
   * `maxLen`, and rejects `len * minElementSize` exceeding the bytes remaining — both
   * before allocating the result array. */
  vec<T>(maxLen: number, minElementSize: number, field: string, decodeOne: () => T): T[] {
    const len = this.u32();
    if (len > maxLen) return fail({ kind: 'tooManyElements', field });
    const needed = len * minElementSize;
    if (needed > this.remaining) return fail({ kind: 'truncated' });
    const result: T[] = new Array(len);
    for (let i = 0; i < len; i++) result[i] = decodeOne();
    return result;
  }
}

// ---------------------------------------------------------------------------
// lod.rs / keyframe_delta.rs types — decode only (per DD5/DD6/wire-protocol.md).
// ---------------------------------------------------------------------------

function decodeActionClass(r: Reader): ActionClass {
  switch (r.u8()) {
    case 0:
      return ActionClass.Forage;
    case 1:
      return ActionClass.Build;
    case 2:
      return ActionClass.Fight;
    case 3:
      return ActionClass.Idle;
    default:
      return fail({ kind: 'invalidDiscriminant', field: 'ActionClass' });
  }
}

function decodeBlobRecord(r: Reader): BlobRecord {
  const blobId = r.u32();
  const centroid = r.tupleU16();
  const count = r.u32();
  const dominantAction = decodeActionClass(r);
  return { blobId, centroid, count, dominantAction };
}

function decodeQueenRecord(r: Reader): QueenRecord {
  const id = r.u32();
  const colonyId = r.u32();
  const position = r.tupleU16();
  return { id, colonyId, position };
}

/** A patch summary record: id u32, position 2xu16, then the quantized `pool` byte
 * (wire-entity-detail). `pool` occupies the byte the former `hasNectar` bool did — but every
 * value 0-255 is valid, so unlike a bool there is no discriminant to reject. */
function decodePatchRecord(r: Reader): PatchRecord {
  const id = r.u32();
  const position = r.tupleU16();
  const pool = r.u8();
  return { id, position, pool };
}

function decodeRuinRecord(r: Reader): RuinRecord {
  const id = r.u32();
  const position = r.tupleU16();
  return { id, position };
}

/** Unpacks the packed facing/action byte: low nibble facing, bits 4-5 the action class, bit 6
 * the `gathering` flag (wire-gathering-bit), bit 7 the one surviving reserved bit — which must
 * be clear, codec.rs's hardening against a hostile/corrupt byte aliasing a plausible-but-wrong
 * orientation or smuggling in an unknown future flag. The guard narrowed with the mask; it was
 * never dropped, and it stays byte-exact with the Rust decoder. */
function decodeBeeRecord(r: Reader): BeeRecord {
  const id = r.u32();
  const position = r.tupleU16();
  const facingAction = r.u8();
  if ((facingAction & 0x80) !== 0) {
    return fail({ kind: 'outOfRange', field: 'BeeRecord.facing_action reserved bits' });
  }
  const facing = facingAction & 0x0f;
  if (facing >= FACING_STEPS) {
    return fail({ kind: 'outOfRange', field: 'BeeRecord.facing' });
  }
  const gathering = (facingAction & 0x40) !== 0;
  // Masked to two bits: bit 6 now sits above the action bits, so the shift alone would fold the
  // gathering flag into the class.
  const actionBits = (facingAction >> 4) & 0x03;
  const action: ActionClass =
    actionBits === 0
      ? ActionClass.Forage
      : actionBits === 1
        ? ActionClass.Build
        : actionBits === 2
          ? ActionClass.Fight
          : ActionClass.Idle;
  // The wire-entity-detail tail, in the Rust encoder's declared order: colony_id u32, the
  // `Option<u8>` type_id (1-byte discriminant then, when Some, the index — `r.option` rejects
  // any other discriminant), then the two quantized-fraction vitals.
  const colonyId = r.u32();
  const typeId = r.option(() => r.u8());
  const health = r.u8();
  const nectar = r.u8();
  return { id, position, facing, action, gathering, colonyId, typeId, health, nectar };
}

function decodeClosePayload(r: Reader): ClosePayload {
  const records = r.vec(MAX_CLOSE_RECORDS, MIN_SIZE_BEE_RECORD, 'ClosePayload.records', () =>
    decodeBeeRecord(r),
  );
  const truncated = r.bool();
  const totalCount = r.u32();
  return { records, truncated, totalCount };
}

/** A close-tier dance record (dance-visuals): dancerId u32, target 2×u16, tagHash u16. */
function decodeDanceRecord(r: Reader): DanceRecord {
  const dancerId = r.u32();
  const target = r.tupleU16();
  const tagHash = r.u16();
  return { dancerId, target, tagHash };
}

/** A close-tier hive-cell record (hive-egg-cells): id u32, position 2×u16, packed state u8.
 * The state byte's three orthogonal flags (bits 0-2) decode to booleans; bits 3-7 are the
 * `combat-strikes-and-damage-flash` five-bit hp bucket (0..31), raw and unconverted — the same
 * discipline `BeeRecord.health` follows for its own 0..255 fraction.
 *
 * **All eight bits are now defined — there is no reserved bit left**, so unlike
 * `decodeBeeRecord`'s facing/action byte (which still rejects a set reserved bit 7) this decode
 * must NOT mask bits 3-7 away: every byte value is a valid `(flags, bucket)` pair, so there is
 * nothing left to reject a hostile/corrupt byte on. A future cell flag becomes a deliberate wire
 * change (a new bit needs somewhere to come from), not a silently-tolerated reserved bit. */
function decodeCellRecord(r: Reader): CellRecord {
  const id = r.u32();
  const position = r.tupleU16();
  const state = r.u8();
  return {
    id,
    position,
    occupied: (state & 0x01) !== 0,
    damaged: (state & 0x02) !== 0,
    ruin: (state & 0x04) !== 0,
    hpBucket: (state >> 3) & 0x1f,
  };
}

/** A close-tier cell payload (hive-egg-cells): a bounded records collection then a truncated
 * bool. */
function decodeCellPayload(r: Reader): CellPayload {
  const records = r.vec(MAX_CELL_RECORDS, MIN_SIZE_CELL_RECORD, 'CellPayload.records', () =>
    decodeCellRecord(r),
  );
  const truncated = r.bool();
  return { records, truncated };
}

function decodeWideUpsert(r: Reader): WideUpsert {
  switch (r.u8()) {
    case 0:
      return { kind: 'blob', value: decodeBlobRecord(r) };
    case 1:
      return { kind: 'queen', value: decodeQueenRecord(r) };
    case 2:
      return { kind: 'patch', value: decodePatchRecord(r) };
    case 3:
      return { kind: 'ruin', value: decodeRuinRecord(r) };
    default:
      return fail({ kind: 'invalidDiscriminant', field: 'WideUpsert' });
  }
}

function decodeCloseUpsert(r: Reader): CloseUpsert {
  switch (r.u8()) {
    case 0:
      return { kind: 'bee', value: decodeBeeRecord(r) };
    case 1:
      return { kind: 'patch', value: decodePatchRecord(r) };
    case 2:
      return { kind: 'dance', value: decodeDanceRecord(r) };
    case 3:
      return { kind: 'cell', value: decodeCellRecord(r) };
    default:
      return fail({ kind: 'invalidDiscriminant', field: 'CloseUpsert' });
  }
}

function decodeWideEntityKey(r: Reader): WideEntityKey {
  const tag = r.u8();
  const id = r.u32();
  switch (tag) {
    case 0:
      return { kind: 'blob', id };
    case 1:
      return { kind: 'queen', id };
    case 2:
      return { kind: 'patch', id };
    case 3:
      return { kind: 'ruin', id };
    default:
      return fail({ kind: 'invalidDiscriminant', field: 'WideEntityKey' });
  }
}

function decodeCloseEntityKey(r: Reader): CloseEntityKey {
  const tag = r.u8();
  const id = r.u32();
  switch (tag) {
    case 0:
      return { kind: 'bee', id };
    case 1:
      return { kind: 'patch', id };
    case 2:
      return { kind: 'dance', id };
    case 3:
      return { kind: 'cell', id };
    default:
      return fail({ kind: 'invalidDiscriminant', field: 'CloseEntityKey' });
  }
}

function decodeWideKeyframe(r: Reader): WideKeyframe {
  const seq = r.u64();
  const tick = r.u32();
  const blobs = r.vec(MAX_WIDE_RECORDS, MIN_SIZE_BLOB_RECORD, 'WideKeyframe.blobs', () =>
    decodeBlobRecord(r),
  );
  const queens = r.vec(MAX_WIDE_RECORDS, MIN_SIZE_QUEEN_RECORD, 'WideKeyframe.queens', () =>
    decodeQueenRecord(r),
  );
  const patches = r.vec(MAX_WIDE_RECORDS, MIN_SIZE_PATCH_RECORD, 'WideKeyframe.patches', () =>
    decodePatchRecord(r),
  );
  const ruins = r.vec(MAX_WIDE_RECORDS, MIN_SIZE_RUIN_RECORD, 'WideKeyframe.ruins', () =>
    decodeRuinRecord(r),
  );
  return { seq, tick, blobs, queens, patches, ruins };
}

function decodeCloseKeyframe(r: Reader): CloseKeyframe {
  const seq = r.u64();
  const tick = r.u32();
  const bees = decodeClosePayload(r);
  const patches = r.vec(MAX_WIDE_RECORDS, MIN_SIZE_PATCH_RECORD, 'CloseKeyframe.patches', () =>
    decodePatchRecord(r),
  );
  const dances = r.vec(MAX_DANCE_RECORDS, MIN_SIZE_DANCE_RECORD, 'CloseKeyframe.dances', () =>
    decodeDanceRecord(r),
  );
  const cells = decodeCellPayload(r);
  return { seq, tick, bees, patches, dances, cells };
}

function decodeKeyframe(r: Reader): Keyframe {
  switch (r.u8()) {
    case 0:
      return { tier: Tier.Wide, ...decodeWideKeyframe(r) };
    case 1:
      return { tier: Tier.Close, ...decodeCloseKeyframe(r) };
    default:
      return fail({ kind: 'invalidDiscriminant', field: 'Keyframe' });
  }
}

function decodeWideDelta(r: Reader): WideDelta {
  const seq = r.u64();
  const baseSeq = r.u64();
  const tick = r.u32();
  const upserts = r.vec(MAX_WIDE_RECORDS, MIN_SIZE_WIDE_UPSERT, 'WideDelta.upserts', () =>
    decodeWideUpsert(r),
  );
  const deletes = r.vec(MAX_WIDE_RECORDS, MIN_SIZE_WIDE_ENTITY_KEY, 'WideDelta.deletes', () =>
    decodeWideEntityKey(r),
  );
  return { seq, baseSeq, tick, upserts, deletes };
}

function decodeCloseDelta(r: Reader): CloseDelta {
  const seq = r.u64();
  const baseSeq = r.u64();
  const tick = r.u32();
  const upserts = r.vec(MAX_CLOSE_RECORDS, MIN_SIZE_CLOSE_UPSERT, 'CloseDelta.upserts', () =>
    decodeCloseUpsert(r),
  );
  const deletes = r.vec(MAX_CLOSE_RECORDS, MIN_SIZE_CLOSE_ENTITY_KEY, 'CloseDelta.deletes', () =>
    decodeCloseEntityKey(r),
  );
  return { seq, baseSeq, tick, upserts, deletes };
}

function decodeDelta(r: Reader): Delta {
  switch (r.u8()) {
    case 0:
      return { tier: Tier.Wide, ...decodeWideDelta(r) };
    case 1:
      return { tier: Tier.Close, ...decodeCloseDelta(r) };
    default:
      return fail({ kind: 'invalidDiscriminant', field: 'Delta' });
  }
}

// ---------------------------------------------------------------------------
// observation.rs types embedded on the wire — decode only.
// ---------------------------------------------------------------------------

function decodeSeason(r: Reader): Season {
  switch (r.u8()) {
    case 0:
      return Season.Summer;
    case 1:
      return Season.Autumn;
    case 2:
      return Season.Winter;
    default:
      return fail({ kind: 'invalidDiscriminant', field: 'Season' });
  }
}

function decodeScoreboardRow(r: Reader): ScoreboardRow {
  const colonyId = r.u32();
  const livingCount = r.u32();
  const pool = r.f32();
  return { colonyId, livingCount, pool };
}

function decodeMatchStateObs(r: Reader): MatchStateObs {
  const season = decodeSeason(r);
  const viabilityRadius = r.f32();
  const clockRemainingTicks = r.u32();
  const eliminations = r.vec(MAX_COLONIES, MIN_SIZE_U32, 'MatchStateObs.eliminations', () =>
    r.u32(),
  );
  const scoreboard = r.vec(
    MAX_COLONIES,
    MIN_SIZE_SCOREBOARD_ROW,
    'MatchStateObs.scoreboard',
    () => decodeScoreboardRow(r),
  );
  return { season, viabilityRadius, clockRemainingTicks, eliminations, scoreboard };
}

// ---------------------------------------------------------------------------
// messages.rs types — decode side (server->client) + encode side (client->server).
// ---------------------------------------------------------------------------

function decodeManifestSummary(r: Reader): ManifestSummary {
  const tickRateHz = r.f32();
  const fieldSize = r.f32();
  const colonyIds = r.vec(MAX_COLONIES, MIN_SIZE_U32, 'ManifestSummary.colony_ids', () =>
    r.u32(),
  );
  const danceRadius = r.f32();
  const playMarginU = r.f32();
  const speedMultiplier = r.f32();
  return { tickRateHz, fieldSize, colonyIds, danceRadius, playMarginU, speedMultiplier };
}

function decodeWelcome(r: Reader): Welcome {
  const tick = r.u32();
  const protocolVersion = r.u16();
  const manifest = decodeManifestSummary(r);
  const yourColony = r.option(() => r.u32());
  // The appended own-hive anchor: a 1-byte Option discriminant then, when Some, two LE f32
  // (`colony-field-canvas`).
  const yourHive = r.option(() => r.tupleF32());
  return { tick, protocolVersion, manifest, yourColony, yourHive };
}

function decodeMatchState(r: Reader): MatchState {
  const tick = r.u32();
  const state = decodeMatchStateObs(r);
  return { tick, state };
}

function decodeTypeMeta(r: Reader): TypeMeta {
  const typeId = r.u8();
  if (typeId >= MAX_TYPES) return fail({ kind: 'outOfRange', field: 'TypeMeta.type_id' });
  const name = r.ident();
  const buffName = r.ident();
  const activeVersion = r.u32();
  const canonicalText = r.text();
  return { typeId, name, buffName, activeVersion, canonicalText };
}

function decodeVersionHead(r: Reader): VersionHead {
  const typeId = r.u8();
  if (typeId >= MAX_TYPES) return fail({ kind: 'outOfRange', field: 'VersionHead.type_id' });
  const headVersion = r.u32();
  return { typeId, headVersion };
}

function decodeColonyMeta(r: Reader): ColonyMeta {
  const tick = r.u32();
  const types = r.vec(MAX_TYPES, MIN_SIZE_TYPE_META, 'ColonyMeta.types', () => decodeTypeMeta(r));
  const birthType = r.u8();
  if (birthType >= MAX_TYPES) return fail({ kind: 'outOfRange', field: 'ColonyMeta.birth_type' });
  const versionHeads = r.vec(
    MAX_VERSION_HEADS,
    MIN_SIZE_VERSION_HEAD,
    'ColonyMeta.version_heads',
    () => decodeVersionHead(r),
  );
  return { tick, types, birthType, versionHeads };
}

function decodePromptStatusKind(r: Reader): PromptStatusKind {
  switch (r.u8()) {
    case 0:
      return { kind: 'idle' };
    case 1:
      return { kind: 'generating' };
    case 2:
      return { kind: 'repairing', attempt: r.u8() };
    case 3:
      return { kind: 'accepted', version: r.u32() };
    case 4: {
      const len = r.u32();
      if (len > MAX_DIAGNOSTICS) {
        return fail({ kind: 'tooManyElements', field: 'PromptStatusKind.diagnostics' });
      }
      if (len > r.remaining) return fail({ kind: 'truncated' });
      const diagnostics: string[] = new Array(len);
      for (let i = 0; i < len; i++) diagnostics[i] = r.text();
      return { kind: 'failed', diagnostics };
    }
    case 5:
      return { kind: 'stale' };
    default:
      return fail({ kind: 'invalidDiscriminant', field: 'PromptStatusKind' });
  }
}

function decodePromptStatus(r: Reader): PromptStatus {
  const tick = r.u32();
  const typeId = r.u8();
  if (typeId >= MAX_TYPES) return fail({ kind: 'outOfRange', field: 'PromptStatus.type_id' });
  const status = decodePromptStatusKind(r);
  return { tick, typeId, status };
}

function decodePlacement(r: Reader): Placement {
  const colonyId = r.u32();
  const rank = r.u32();
  const livingCount = r.u32();
  const pool = r.f32();
  return { colonyId, rank, livingCount, pool };
}

function decodeMatchResult(r: Reader): MatchResult {
  const tick = r.u32();
  const placements = r.vec(MAX_PLACEMENTS, MIN_SIZE_PLACEMENT, 'MatchResult.placements', () =>
    decodePlacement(r),
  );
  const timelineRef = r.text();
  return { tick, placements, timelineRef };
}

function decodeStateBucket(r: Reader): StateBucket {
  const stateName = r.ident();
  const count = r.u32();
  return { stateName, count };
}

function decodeTypeHistogram(r: Reader): TypeHistogram {
  const typeId = r.u8();
  if (typeId >= MAX_TYPES) return fail({ kind: 'outOfRange', field: 'TypeHistogram.type_id' });
  const buckets = r.vec(MAX_STATE_BUCKETS, MIN_SIZE_STATE_BUCKET, 'TypeHistogram.buckets', () =>
    decodeStateBucket(r),
  );
  return { typeId, buckets };
}

function decodeStateHistogram(r: Reader): StateHistogram {
  const tick = r.u32();
  const perType = r.vec(MAX_TYPES, MIN_SIZE_TYPE_HISTOGRAM, 'StateHistogram.per_type', () =>
    decodeTypeHistogram(r),
  );
  return { tick, perType };
}

function decodeRejectReason(r: Reader): RejectReason {
  switch (r.u8()) {
    case 0:
      return { kind: 'upgrade', minVersion: r.u16() };
    case 1:
      return { kind: 'unauthorized' };
    case 2:
      return { kind: 'rateLimited' };
    case 3:
      return { kind: 'stale' };
    case 4:
      return { kind: 'invalid' };
    case 5: {
      const len = r.u32();
      if (len > MAX_DIAGNOSTICS) {
        return fail({ kind: 'tooManyElements', field: 'RejectReason.diagnostics' });
      }
      if (len > r.remaining) return fail({ kind: 'truncated' });
      const diagnostics: string[] = new Array(len);
      for (let i = 0; i < len; i++) diagnostics[i] = r.text();
      return { kind: 'compileFailed', diagnostics };
    }
    default:
      return fail({ kind: 'invalidDiscriminant', field: 'RejectReason' });
  }
}

function decodeRejected(r: Reader): Rejected {
  const tick = r.u32();
  const reason = decodeRejectReason(r);
  return { tick, reason };
}

// --- client -> server encode ---

function encodeHello(w: Writer, h: Hello): void {
  w.u16(h.protocolVersion);
  w.string(h.sessionToken);
  w.string(h.matchId);
}

function encodeViewportRect(w: Writer, v: ViewportRect): void {
  w.f32(v.min[0]);
  w.f32(v.min[1]);
  w.f32(v.max[0]);
  w.f32(v.max[1]);
}

function encodeTier(w: Writer, t: Tier): void {
  w.u8(t === Tier.Wide ? 0 : 1);
}

function encodeSubscribe(w: Writer, s: Subscribe): void {
  encodeViewportRect(w, s.viewport);
  encodeTier(w, s.tier);
}

function encodePrompt(w: Writer, p: Prompt): void {
  w.string(p.typeName);
  w.string(p.promptText);
}

function encodeHandEdit(w: Writer, h: HandEdit): void {
  w.string(h.typeName);
  w.string(h.programSource);
  w.u32(h.baseVersion);
}

function encodeRollback(w: Writer, r: Rollback): void {
  w.string(r.typeName);
  w.u32(r.targetVersion);
}

function encodeSetBirthType(w: Writer, s: SetBirthType): void {
  w.string(s.typeName);
}

function encodeBuffName(w: Writer, b: number): void {
  w.u8(b);
}

function encodeInitialProgram(w: Writer, ip: InitialProgram): void {
  if (ip.kind === 'source') {
    w.u8(0);
    w.string(ip.source);
  } else {
    w.u8(1);
    w.string(ip.preset);
  }
}

function encodeCreateType(w: Writer, c: CreateType): void {
  w.string(c.name);
  encodeBuffName(w, c.buff);
  encodeInitialProgram(w, c.initialProgram);
}

function encodeAck(w: Writer, a: Ack): void {
  w.u64(a.highestContiguousSeq);
}

/** Encodes a `ReplayControl` transport verb (PROTOCOL §3; the `waggle-proto`
 * `ReplayControl` enum order — `Pause` 0, `Resume` 1, `SetSpeed { multiplier }` 2 then
 * the f32 LE multiplier, `Restart` 3). Spectator-only; no colony-mutation payload. */
function encodeReplayControl(w: Writer, c: ReplayControlMessage): void {
  switch (c.kind) {
    case 'pause':
      w.u8(0);
      break;
    case 'resume':
      w.u8(1);
      break;
    case 'setSpeed':
      w.u8(2);
      w.f32(c.multiplier);
      break;
    case 'restart':
      w.u8(3);
      break;
  }
}

// ---------------------------------------------------------------------------
// Top-level catalogue encode/decode: the leading 1-byte message tag.
// ---------------------------------------------------------------------------

export function encodeClientMessage(msg: ClientMessage): Uint8Array {
  const w = new Writer();
  switch (msg.type) {
    case 'hello':
      w.u8(0);
      encodeHello(w, msg.value);
      break;
    case 'subscribe':
      w.u8(1);
      encodeSubscribe(w, msg.value);
      break;
    case 'prompt':
      w.u8(2);
      encodePrompt(w, msg.value);
      break;
    case 'handEdit':
      w.u8(3);
      encodeHandEdit(w, msg.value);
      break;
    case 'rollback':
      w.u8(4);
      encodeRollback(w, msg.value);
      break;
    case 'setBirthType':
      w.u8(5);
      encodeSetBirthType(w, msg.value);
      break;
    case 'createType':
      w.u8(6);
      encodeCreateType(w, msg.value);
      break;
    case 'ack':
      w.u8(7);
      encodeAck(w, msg.value);
      break;
    case 'replayControl':
      w.u8(8);
      encodeReplayControl(w, msg.value);
      break;
  }
  return w.finish();
}

/** Decodes a `ServerMessage`: rejects input over `MAX_MESSAGE_SIZE` before decoding any
 * field, an unknown leading tag before reading any field, and trailing bytes after a
 * complete message — never a thrown string, always a typed `DecodeError`. */
export function decodeServerMessage(bytes: Uint8Array): DecodeResult<ServerMessage> {
  if (bytes.length > MAX_MESSAGE_SIZE) {
    return { ok: false, error: { kind: 'oversized' } };
  }
  try {
    const r = new Reader(bytes);
    const tag = r.u8();
    let value: ServerMessage;
    switch (tag) {
      case 0:
        value = { type: 'welcome', value: decodeWelcome(r) };
        break;
      case 1:
        value = { type: 'keyframe', value: decodeKeyframe(r) };
        break;
      case 2:
        value = { type: 'delta', value: decodeDelta(r) };
        break;
      case 3:
        value = { type: 'matchState', value: decodeMatchState(r) };
        break;
      case 4:
        value = { type: 'colonyMeta', value: decodeColonyMeta(r) };
        break;
      case 5:
        value = { type: 'promptStatus', value: decodePromptStatus(r) };
        break;
      case 6:
        value = { type: 'result', value: decodeMatchResult(r) };
        break;
      case 7:
        value = { type: 'rejected', value: decodeRejected(r) };
        break;
      case 8:
        value = { type: 'stateHistogram', value: decodeStateHistogram(r) };
        break;
      default:
        return { ok: false, error: { kind: 'unknownTag', tag } };
    }
    if (r.remaining !== 0) {
      return { ok: false, error: { kind: 'trailingBytes' } };
    }
    return { ok: true, value };
  } catch (e) {
    if (e instanceof DecodeFailure) {
      return { ok: false, error: e.error };
    }
    throw e;
  }
}

// ---------------------------------------------------------------------------
// Quantization (design D5, lod::quantize/dequantize contract).
// ---------------------------------------------------------------------------

/** Round-half-to-even ("banker's rounding"), matching Rust's `f64::round_ties_even`. */
function roundTiesEven(x: number): number {
  const floor = Math.floor(x);
  const diff = x - floor;
  if (diff < 0.5) return floor;
  if (diff > 0.5) return floor + 1;
  return floor % 2 === 0 ? floor : floor + 1;
}

/** Quantize a centred-field-frame coordinate to a u16 (returns null on error). */
export function quantize(coord: number, fieldSize: number): number | null {
  const c = Math.fround(coord);
  const fs = Math.fround(fieldSize);
  if (!Number.isFinite(fs) || fs <= 0 || !Number.isFinite(c)) return null;
  const half = fs / 2;
  let norm = (c + half) / fs;
  norm = Math.min(1, Math.max(0, norm));
  return roundTiesEven(norm * 65535);
}

/** Dequantize a u16 back to a coordinate (returns null on error). */
export function dequantize(q: number, fieldSize: number): number | null {
  const fs = Math.fround(fieldSize);
  if (!Number.isFinite(fs) || fs <= 0) return null;
  const half = fs / 2;
  const coord = (q / 65535) * fs - half;
  return Math.fround(coord);
}
